/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';
import { EntityScoreExtended } from '../../api/types';
import { domainKey } from './applyBoardScope';
import { aggregateScores } from './aggregateScores';
import { DomainTree, topLevelOf } from './useDomainTree';

/**
 * A row of the board (feature 0058). Either a System exactly as IMC sent it,
 * or a **group row**: a subdomain standing in for the Systems inside it, with
 * the same shape so every renderer, the KPI strip and the trend treat the two
 * alike.
 *
 * A group row is an `EntityScoreExtended` pointing at the Domain entity, so
 * `scoreRoute` links it to the Domain's own Score tab with no special case,
 * and its `domain` is its **top-level** ancestor — what the board's Domain
 * control scopes on.
 */
export interface BoardRow extends EntityScoreExtended {
  /** Present only on a group row: what it aggregates. */
  group?: {
    /** The subdomain this row stands for ({@link domainKey}). */
    domainKey: string;
    /** Members in the group — Systems, and nested group rows. */
    memberCount: number;
    /** Members that had a Total — the mean's denominator. */
    scoredCount: number;
  };
  /** Present only on a group row: its direct members, as rows. */
  children?: BoardRow[];
  /** The `id` of the group row this row sits under; absent at the top level. */
  parentId?: string;
}

/** Whether this row stands for a subdomain rather than a System. */
export const isGroupRow = (row: BoardRow): boolean => row.group !== undefined;

/**
 * Every row of the tree in one array — group rows followed by their members,
 * at any depth. What material-table's `parentChildData` needs; the board's
 * own consumers (KPI strip, trend) take the top-level array instead.
 */
export function flattenBoardRows(rows: BoardRow[]): BoardRow[] {
  const flat: BoardRow[] = [];
  const visit = (row: BoardRow) => {
    flat.push(row);
    for (const child of row.children ?? []) visit(child);
  };
  rows.forEach(visit);
  return flat;
}

/**
 * The outermost subdomain above (or equal to) `key`, i.e. the one whose own
 * parent is top-level — the group row a System in `key` ends up inside.
 * `undefined` when `key` is not a subdomain at all.
 *
 * Cycle-safe: a `a → b → a` pair in the catalog stops at the first key seen
 * twice rather than looping. No row may be lost to a mistake in two YAML files.
 */
function rootSubdomainOf(key: string, tree: DomainTree): string | undefined {
  if (!tree.parents.has(key)) return undefined;
  const seen = new Set<string>([key]);
  let current = key;
  for (;;) {
    const parent = tree.parents.get(current);
    if (parent === undefined || seen.has(parent) || !tree.parents.has(parent)) {
      return current;
    }
    seen.add(parent);
    current = parent;
  }
}

/** The display name of a group row: the Domain's title, else its raw name. */
const groupTitle = (key: string, tree: DomainTree): string =>
  tree.titles.get(key) ?? key;

/**
 * Collapses the Systems of each subdomain into one row carrying them as
 * children (feature 0058).
 *
 * Systems on a top-level Domain, and Systems with no Domain at all, pass
 * through untouched — the board is a list of Systems that happens to have a
 * few group rows in it, not a different board.
 *
 * Nesting recurses: a subdomain of a subdomain becomes a group row inside its
 * parent's group row, and a parent averages its **direct** children (a large
 * subdomain must not quietly dominate its parent's number). Nothing is
 * designed for a depth the catalog does not have, but nothing hard-codes two
 * levels either.
 *
 * Ordering is the input's: a group row takes the position of its first member,
 * and the rest of that group's members leave the top level. The table sorts
 * afterwards anyway; what matters is that the same data always yields the same
 * array.
 */
export function buildBoardRows(
  scores: EntityScoreExtended[],
  tree: DomainTree | undefined,
): BoardRow[] {
  // No hierarchy known (still loading, no catalog API, or simply no
  // subdomains): the board is the flat list it has always been.
  if (!tree || tree.parents.size === 0) {
    return scores;
  }

  // Systems directly under each subdomain, in input order.
  const directMembers = new Map<string, EntityScoreExtended[]>();
  // Subdomains directly under each Domain, in the tree's order.
  const childSubdomains = new Map<string, string[]>();
  for (const [child, parent] of tree.parents) {
    const siblings = childSubdomains.get(parent);
    if (siblings) siblings.push(child);
    else childSubdomains.set(parent, [child]);
  }

  // One pass over the scores decides each one's slot: a System keeps its own,
  // a grouped System gives its slot to its group the first time one is seen.
  type Slot =
    | { kind: 'system'; row: EntityScoreExtended }
    | { kind: 'group'; key: string };
  const slots: Slot[] = [];
  const emitted = new Set<string>();
  for (const score of scores) {
    const key = score.domain ? domainKey(score.domain) : undefined;
    const root = key ? rootSubdomainOf(key, tree) : undefined;
    if (!key || !root) {
      slots.push({ kind: 'system', row: score });
      continue;
    }
    const members = directMembers.get(key);
    if (members) members.push(score);
    else directMembers.set(key, [score]);
    if (!emitted.has(root)) {
      emitted.add(root);
      slots.push({ kind: 'group', key: root });
    }
  }

  const buildGroup = (
    key: string,
    visiting: Set<string>,
  ): BoardRow | undefined => {
    if (visiting.has(key)) return undefined;
    visiting.add(key);
    const children: BoardRow[] = [
      ...(directMembers.get(key) ?? []),
      ...(childSubdomains.get(key) ?? [])
        .map(child => buildGroup(child, visiting))
        .filter((row): row is BoardRow => row !== undefined),
    ];
    visiting.delete(key);
    // No empty rows: a subdomain nothing lives under is not a row, exactly as
    // a Domain no System carries is not an option in the Domain control.
    if (children.length === 0) return undefined;

    const aggregate = aggregateScores(children);
    const namespace = tree.namespaces.get(key) ?? DEFAULT_NAMESPACE;
    const entityRef = { kind: 'domain', namespace, name: key };
    const id = `domain:${namespace}/${key}`;
    const row: BoardRow = {
      id,
      entityRef,
      // The newest of the members': a group is as fresh as its freshest part,
      // and this feeds only the "generated at" wording.
      generatedDateTimeUtc: children
        .map(child => String(child.generatedDateTimeUtc))
        .sort()
        .slice(-1)[0],
      scorePercent: aggregate.scorePercent,
      scoreSuccess: aggregate.scoreSuccess,
      scoringReviewer: null,
      scoringReviewDate: null,
      pillars: aggregate.pillars,
      owner: undefined,
      reviewer: undefined,
      reviewDate: undefined,
      title: groupTitle(key, tree),
      // The TOP-LEVEL ancestor, not the subdomain itself: this is the field
      // the Domain control scopes on, and the subtitle a reader sees under the
      // group's name.
      domain: topLevelOf(key, tree),
      inCatalog: true,
      group: {
        domainKey: key,
        memberCount: aggregate.memberCount,
        scoredCount: aggregate.scoredCount,
      },
      children: children.map(child => ({ ...child, parentId: id })),
    };
    return row;
  };

  const built = new Map<string, BoardRow | undefined>();
  for (const root of emitted) {
    built.set(root, buildGroup(root, new Set()));
  }

  return slots.flatMap(slot => {
    if (slot.kind === 'system') return [slot.row];
    const group = built.get(slot.key);
    // `undefined` is unreachable — a slot exists only because a member did —
    // but a group that somehow built to nothing leaves no empty row behind.
    return group ? [group] : [];
  });
}
